import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {Tracker} from '@udemy/event-tracking';
import {Course, CourseEnrollment} from '@udemy/graphql';
import {useMatchMedia} from '@udemy/hooks';
import {Confetti} from '@udemy/react-messaging-components';
import {
    ContinueCourseCta,
    DefaultCta,
    RecommendedCourseCta,
    YearOneRecapComponent,
} from '@udemy/subscription-browse';

import {SubscriberEngagementRecapCallToActionType} from 'src/generated/graphql';
import {type SubscriberEngagementRecapData} from 'src/types/page-props';

import {
    getSubscriberEngagementRecapStats,
    MIN_STATS_THRESHOLD,
    SUBSCRIBER_ENGAGEMENT_RECAP_CONFETTI_SEEN_KEY,
    SUBSCRIBER_ENGAGEMENT_RECAP_VISIBILITY_KEY,
    SubscriberEngagementRecapCTAClickEvent,
    SubscriberEngagementRecapMetricPresentedEvent,
    SubscriberEngagementRecapPresentedEvent,
    SubscriberEngagementRecapStatsType,
} from '../hooks/subscriber-engagement-recap';

const CTA_COMPONENT_MAP = {
    [SubscriberEngagementRecapCallToActionType.ContinueCourse]: ContinueCourseCta,
    [SubscriberEngagementRecapCallToActionType.RecommendedCourse]: RecommendedCourseCta,
} as Record<SubscriberEngagementRecapCallToActionType, React.ComponentType<object>>;

export interface YearOneRecapSectionProps {
    subscriberEngagementRecapData: NonNullable<SubscriberEngagementRecapData>;
}

export function YearOneRecapSection({
    subscriberEngagementRecapData,
}: YearOneRecapSectionProps): React.ReactElement | null {
    const {callToAction, courseEnrollment} = subscriberEngagementRecapData;

    const [isComponentVisible, setIsComponentVisible] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    const isDesktop = Boolean(useMatchMedia('lg-min'));

    const stats = useMemo(
        () => getSubscriberEngagementRecapStats(subscriberEngagementRecapData),
        [subscriberEngagementRecapData],
    );
    const hasMinimalStats = Object.keys(stats).length >= MIN_STATS_THRESHOLD;
    const {topicsExplored, ...otherStats} = stats;

    const handleClose = useCallback(() => {
        localStorage.setItem(SUBSCRIBER_ENGAGEMENT_RECAP_VISIBILITY_KEY, 'false');
        setIsComponentVisible(false);
    }, []);

    const handleCtaClick = useCallback((ctaType: SubscriberEngagementRecapCallToActionType) => {
        Tracker.publishEvent(new SubscriberEngagementRecapCTAClickEvent({ctaType}));
    }, []);

    const dynamicCta = useMemo<React.ReactNode | undefined>(() => {
        if (!callToAction) return undefined;

        const commonProps = {
            title: callToAction.title,
            description: callToAction.description,
            url: callToAction.url,
            onClick: () => handleCtaClick(callToAction.type),
        };

        const props =
            {
                [SubscriberEngagementRecapCallToActionType.ContinueCourse]: {
                    ...commonProps,
                    title: gettext('Pick Up Where You Left Off'),
                    enrollment: courseEnrollment as CourseEnrollment,
                },
                [SubscriberEngagementRecapCallToActionType.RecommendedCourse]: {
                    ...commonProps,
                    title: gettext('Recommended for You'),
                    course: courseEnrollment?.course as Course,
                },
                [SubscriberEngagementRecapCallToActionType.RenewSubscription]: {
                    ...commonProps,
                    title: gettext('Stay on Track to Success'),
                    description: gettext(
                        "You've made incredible progress. Renew your subscription to keep building the skills that matter.",
                    ),
                    buttonText: gettext('Renew Now'),
                },
                [SubscriberEngagementRecapCallToActionType.TryAssessments]: {
                    ...commonProps,
                    title: gettext('Test Your Knowledge'),
                    description: gettext(
                        "Put your skills to the test with Assessments. See what you've mastered and what's next.",
                    ),
                    buttonText: gettext('Try It Now'),
                },
            }[callToAction.type] ?? commonProps;

        const Component = CTA_COMPONENT_MAP[callToAction.type] ?? DefaultCta;

        return <Component {...props} />;
    }, [callToAction, courseEnrollment, handleCtaClick]);

    useEffect(() => {
        const isYearOneRecapVisible =
            localStorage.getItem(SUBSCRIBER_ENGAGEMENT_RECAP_VISIBILITY_KEY) !== 'false';
        const hasConfettiBeenShown =
            localStorage.getItem(SUBSCRIBER_ENGAGEMENT_RECAP_CONFETTI_SEEN_KEY) === 'true';

        setIsComponentVisible(isYearOneRecapVisible);

        if (isYearOneRecapVisible && hasMinimalStats && callToAction) {
            Tracker.publishEvent(
                new SubscriberEngagementRecapPresentedEvent({
                    ctaType: callToAction.type,
                }),
            );
            (Object.keys(stats) as SubscriberEngagementRecapStatsType[]).forEach((metric) => {
                Tracker.publishEvent(new SubscriberEngagementRecapMetricPresentedEvent({metric}));
            });
        }
        if (isYearOneRecapVisible && hasMinimalStats && !hasConfettiBeenShown) {
            localStorage.setItem(SUBSCRIBER_ENGAGEMENT_RECAP_CONFETTI_SEEN_KEY, 'true');
            setShowConfetti(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!(isComponentVisible && hasMinimalStats)) return null;

    return (
        <div className="component-margin" data-purpose="year-one-recap-section">
            {showConfetti && <Confetti usePortal />}
            <YearOneRecapComponent
                mobileLayout={!isDesktop}
                inlineSummaryWithStats={isDesktop && Object.keys(otherStats).length <= 3}
                subscriberEngagementRecap={otherStats}
                topicsExplored={topicsExplored}
                onClose={handleClose}
                dynamicCta={dynamicCta}
            />
        </div>
    );
}
